import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "react-query";
import actions from "actions";
import { getToggleUserActivationLoading, getToggleUserActivationSuccess } from "selectors";

interface UseUserDeactivationOptions {
  userId: number;
  isDesignatedCaregiver: boolean;
  onSuccess?: () => void;
  onInvalidateCache?: (queryClient: any) => void;
}

interface UseUserDeactivationReturn {
  // State
  simpleDeactivationDialogOpen: boolean;
  caregiverDeactivationDialogOpen: boolean;
  caregiverReassigned: boolean;
  toggleUserActivationLoading: boolean;
  toggleUserActivationSuccess: boolean;

  // Handlers
  handleOpenDeactivationDialog: () => void;
  handleCloseSimpleDeactivationDialog: () => void;
  handleCloseCaregiverDeactivationDialog: () => void;
  handleDeactivate: () => void;
}

/**
 * Custom hook for user deactivation logic
 * Handles dialog state, designated caregiver detection, and deactivation actions
 */
export const useUserDeactivation = ({
  userId,
  isDesignatedCaregiver,
  onSuccess,
  onInvalidateCache,
}: UseUserDeactivationOptions): UseUserDeactivationReturn => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [simpleDeactivationDialogOpen, setSimpleDeactivationDialogOpen] = useState(false);
  const [caregiverDeactivationDialogOpen, setCaregiverDeactivationDialogOpen] = useState(false);
  const [caregiverReassigned, setCaregiverReassigned] = useState(false);

  const toggleUserActivationLoading = useSelector(getToggleUserActivationLoading) as boolean;
  const toggleUserActivationSuccess = useSelector(getToggleUserActivationSuccess) as boolean;

  // Auto-close dialogs and trigger success callback when deactivation succeeds
  useEffect(() => {
    if (toggleUserActivationSuccess) {
      // Close whichever dialog is open
      if (simpleDeactivationDialogOpen) {
        setSimpleDeactivationDialogOpen(false);
      }
      if (caregiverDeactivationDialogOpen) {
        setCaregiverDeactivationDialogOpen(false);
        setCaregiverReassigned(false);
      }

      if (onInvalidateCache) {
        onInvalidateCache(queryClient);
      }

      if (onSuccess) {
        onSuccess();
      }
      // Clear the success state and trigger callback
      dispatch(actions.clearToggleUserActivation());
    }
  }, [
    toggleUserActivationSuccess,
    dispatch,
    queryClient,
    onInvalidateCache,
    onSuccess,
    simpleDeactivationDialogOpen,
    caregiverDeactivationDialogOpen,
  ]);

  // Auto-switch from caregiver dialog to simple dialog after reassignment
  useEffect(() => {
    if (caregiverReassigned && caregiverDeactivationDialogOpen) {
      // Caregiver has been reassigned, switch to simple deactivation dialog
      setCaregiverDeactivationDialogOpen(false);
      setSimpleDeactivationDialogOpen(true);
    }
  }, [caregiverReassigned]);

  const handleOpenDeactivationDialog = () => {
    if (isDesignatedCaregiver) {
      setCaregiverDeactivationDialogOpen(true);
    } else {
      setSimpleDeactivationDialogOpen(true);
    }
  };

  const handleCloseSimpleDeactivationDialog = () => {
    // Dialog will auto-close on success via useEffect
    // This handler is for manual close (Cancel button)
    if (!toggleUserActivationSuccess) {
      setSimpleDeactivationDialogOpen(false);
    }
  };

  const handleCloseCaregiverDeactivationDialog = () => {
    // Dialog will auto-close on success via useEffect
    // This handler is for manual close (Close button)
    if (!toggleUserActivationSuccess) {
      setCaregiverDeactivationDialogOpen(false);
      setCaregiverReassigned(false);
    }
  };

  const handleDeactivate = () => {
    if (userId) {
      dispatch(actions.toggleUserActivation({ id: userId }));
    }
  };

  return {
    // State
    simpleDeactivationDialogOpen,
    caregiverDeactivationDialogOpen,
    caregiverReassigned,
    toggleUserActivationLoading,
    toggleUserActivationSuccess,

    // Handlers
    handleOpenDeactivationDialog,
    handleCloseSimpleDeactivationDialog,
    handleCloseCaregiverDeactivationDialog,
    handleDeactivate,
  };
};
